import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Grid, Paper, CircularProgress, Divider,
  Checkbox, FormControlLabel, Chip, IconButton, Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import { gwtStyles } from '../../config/theme';

/**
 * System Summary admin tab — SystemSummaryPanel equivalent.
 * Dashboard with PACS system stats, health indicators, and auto-refresh.
 */
export const SystemSummaryPanel = () => {
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: summary, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: queryKeys.systemSummary,
    queryFn: () => api.getSystemSummary(),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(
        () => queryClient.invalidateQueries({ queryKey: queryKeys.systemSummary }),
        10_000,
      );
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, queryClient]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="error" sx={{ fontSize: 12 }}>
          Failed to load system summary: {String(error)}
        </Typography>
      </Box>
    );
  }

  const lastUpdate = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : '';

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        backgroundColor: gwtStyles.tabContent,
      }}
    >
      {/* Title + controls */}
      <Box
        sx={{
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600, color: gwtStyles.tabTitle }}>
            System Summary
          </Typography>
          <Tooltip title="Refresh now">
            <IconButton
              size="small"
              onClick={() => queryClient.invalidateQueries({ queryKey: queryKeys.systemSummary })}
              sx={{ width: 24, height: 24 }}
            >
              <RefreshIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} sx={{ p: 0.25 }} />}
            label={<Typography sx={{ fontSize: 10 }}>Auto (10s)</Typography>}
            sx={{ ml: 0 }}
          />
          {lastUpdate && (
            <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
              Updated: {lastUpdate}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Product info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Product">
              <StatRow label="Name" value={summary?.name ?? '—'} />
              <StatRow label="Version" value={summary?.version ?? '—'} />
              <StatRow label="Build" value={summary?.build ?? '—'} />
              <StatRow label="Copyright" value={summary?.copyright ?? '—'} />
            </StatCard>
          </Grid>

          {/* System (OS, Java, processors, memory) */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="System">
              <StatRow label="OS" value={summary?.systemSummary?.os ?? '—'} />
              <StatRow label="Java" value={summary?.systemSummary?.java ?? '—'} />
              <StatRow label="Processors" value={String(summary?.systemSummary?.processors ?? '—')} />
              <StatRow label="Started" value={summary?.systemSummary?.started ?? '—'} />
            </StatCard>
          </Grid>

          {/* Memory — JVM heap */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Memory">
              {(() => {
                const sys = summary?.systemSummary;
                if (!sys) return <StatRow label="Total" value="—" />;
                const total = sys.totalMemory ?? 0;
                const free = sys.freeMemory ?? 0;
                const max = sys.maxMemory ?? 0;
                const used = total - free;
                const pct = max > 0 ? Math.round((used / max) * 100) : 0;
                return (
                  <>
                    <StatRow label="Used" value={fmtBytes(used)} />
                    <StatRow label="Free" value={fmtBytes(free)} />
                    <StatRow label="Total Allocated" value={fmtBytes(total)} />
                    <StatRow label="Max" value={fmtBytes(max)} />
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ height: 6, borderRadius: 3, backgroundColor: gwtStyles.borderSubtle, overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${pct}%`, backgroundColor: pct > 90 ? '#ff6b6b' : pct > 70 ? '#ffa94d' : '#51cf66', borderRadius: 3 }} />
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>{pct}% heap used</Typography>
                    </Box>
                  </>
                );
              })()}
            </StatCard>
          </Grid>

          {/* Storage */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Storage">
              <StatRow label="Type" value={summary?.storeSummary?.storeType ?? '—'} />
              <StatRow label="Name" value={summary?.storeSummary?.storeName ?? '—'} />
              {(() => {
                const store = summary?.storeSummary;
                if (!store) return null;
                const total = typeof store.totalSpace === 'number' ? store.totalSpace : 0;
                const free = typeof store.freeSpace === 'number' ? store.freeSpace : 0;
                if (total === 0 && free === 0) return null;
                const used = total - free;
                const pct = total > 0 ? Math.round((used / total) * 100) : 0;
                return (
                  <>
                    <StatRow label="Used" value={fmtBytes(used)} />
                    <StatRow label="Free" value={fmtBytes(free)} />
                    <StatRow label="Total" value={fmtBytes(total)} />
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ height: 6, borderRadius: 3, backgroundColor: gwtStyles.borderSubtle, overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${pct}%`, backgroundColor: pct > 90 ? '#ff6b6b' : pct > 70 ? '#ffa94d' : '#51cf66', borderRadius: 3 }} />
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>{pct}% disk used</Typography>
                    </Box>
                  </>
                );
              })()}
            </StatCard>
          </Grid>

          {/* Health */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Health">
              <HealthRow label="Memory" status={summary?.healthSummary?.memoryStatus} />
              <HealthRow label="Store" status={summary?.healthSummary?.storeStatus} />
              <HealthRow label="Transaction Store" status={summary?.healthSummary?.transactionStoreStatus} />
              <HealthRow label="Transaction Queue" status={summary?.healthSummary?.transactionQueueStatus} />
              <HealthRow label="Database" status={summary?.healthSummary?.databaseStatus} />
            </StatCard>
          </Grid>

          {/* Sessions */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Sessions">
              <StatRow label="Active Sessions" value={String(summary?.sessionSummary?.activeCount ?? '—')} />
            </StatCard>
          </Grid>

          {/* Study Cache */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Study Cache">
              <SummarySection data={summary?.studySummary} />
            </StatCard>
          </Grid>

          {/* Patient Cache */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Patient Cache">
              <SummarySection data={summary?.patientSummary} />
            </StatCard>
          </Grid>

          {/* Messages */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Messages">
              <SummarySection data={summary?.messageSummary} />
            </StatCard>
          </Grid>

          {/* Database */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Database">
              <SummarySection data={summary?.databaseSummary} />
            </StatCard>
          </Grid>

          {/* Transaction Store */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Transaction Store">
              <SummarySection data={summary?.transactionStoreSummary} />
            </StatCard>
          </Grid>

          {/* Jobs */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Jobs">
              <SummarySection data={summary?.jobSummary} />
            </StatCard>
          </Grid>

          {/* Cluster */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Cluster">
              <SummarySection data={summary?.clusterSummary} />
            </StatCard>
          </Grid>

          {/* Locations */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Locations">
              <SummarySection data={summary?.locationSummary} />
            </StatCard>
          </Grid>

          {/* Remote Servers */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard title="Remote Servers">
              <SummarySection data={summary?.remoteServers} />
            </StatCard>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

/* ── Sub-components ── */

const StatCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      borderRadius: 1,
      borderColor: 'divider',
      backgroundColor: gwtStyles.popupBg,
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12, mb: 0.5, color: gwtStyles.accentPrimary }}>
      {title}
    </Typography>
    <Divider sx={{ mb: 0.75 }} />
    {children}
  </Paper>
);

const StatRow = ({ label, value, chip }: { label: string; value: string; chip?: boolean }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
    <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
      {label}
    </Typography>
    {chip ? (
      <Chip label={value} size="small" sx={{ fontSize: 10, height: 18 }} />
    ) : (
      <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 500 }}>
        {value}
      </Typography>
    )}
  </Box>
);

/** Health status row with color indicator */
const HealthRow = ({ label, status }: { label: string; status?: string }) => {
  const ok = status === 'OK';
  const color = ok ? '#51cf66' : status === 'Degraded' ? '#ff6b6b' : gwtStyles.textMuted;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
      <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
        <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 500, color }}>{status ?? '—'}</Typography>
      </Box>
    </Box>
  );
};

/** Render an arbitrary key-value summary section from backend JSON */
const SummarySection = ({ data }: { data?: Record<string, unknown> }) => {
  if (!data || Object.keys(data).length === 0) {
    return <Typography variant="caption" sx={{ color: 'text.secondary' }}>No data</Typography>;
  }
  return (
    <>
      {Object.entries(data).map(([key, val]) => {
        // Skip nested objects/arrays — render only scalar values
        if (val !== null && typeof val === 'object') return null;
        const display = typeof val === 'number' && val > 1_000_000
          ? fmtBytes(val)
          : String(val ?? '—');
        return <StatRow key={key} label={camelToLabel(key)} value={display} />;
      })}
    </>
  );
};

/** Format bytes into human-readable string */
function fmtBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

/** Convert camelCase key to readable label */
function camelToLabel(s: string): string {
  return s
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}
